<template>
  <div class="grid-x grid-padding-x board-price-calculator">
    <div class="cell small-12">
      <div class="title-row">
        <h2 class="h3">
          {{ $t('BoardPriceCalculator.title') }}
        </h2>
      </div>
    </div>

    <!-- Board Configuration (left) -->
    <div class="cell small-12 medium-8 margin-top-1">
      <!-- Layer Count -->
      <div class="grid-x grid-margin-x align-middle">
        <div class="cell small-12 medium-4 align-middle">
          {{ $t('BoardPriceCalculator.layerCount') }}
        </div>
        <div class="cell small-12 medium-auto align-middle">
          <div class="button-group">
            <button
              v-for="layer in layerOptions"
              :key="layer"
              type="button"
              class="button secondary hollow pill-button"
              :class="{ 'active': layerCount === layer }"
              @click="setLayerCount(layer)"
            >
              {{ layer }}
            </button>
          </div>
        </div>
      </div>

      <!-- Dimensions (Width + Height in one row) -->
      <div class="grid-x grid-margin-x align-middle">
        <div class="cell small-12 medium-4 align-middle">
          {{ $t('BoardPriceCalculator.dimensions') }}
        </div>
        <div class="cell small-12 medium-auto align-middle">
          <div class="dimensions-row">
            <input
              v-model="width"
              type="number"
              min="0"
              step="0.1"
              class="inline-input"
              :placeholder="$t('BoardPriceCalculator.width')"
              @input="debouncedFetchPrices"
            >
            <span class="dimensions-separator">&times;</span>
            <input
              v-model="height"
              type="number"
              min="0"
              step="0.1"
              class="inline-input"
              :placeholder="$t('BoardPriceCalculator.height')"
              @input="debouncedFetchPrices"
            >
          </div>
        </div>
      </div>

      <!-- Quantity -->
      <div class="grid-x grid-margin-x align-middle">
        <div class="cell small-12 medium-4 align-middle">
          {{ $t('BoardPriceCalculator.quantity') }}
        </div>
        <div class="cell small-12 medium-auto align-middle">
          <input
            v-model.number="quantity"
            type="number"
            min="1"
            step="1"
            class="inline-input"
            :placeholder="$t('BoardPriceCalculator.quantityPlaceholder')"
            @input="debouncedFetchPrices"
          >
        </div>
      </div>

      <!-- Board Config Options -->
      <div
        v-for="config in boardConfigOptions"
        :key="config.key"
        class="grid-x grid-margin-x align-middle"
      >
        <div class="cell small-12 medium-4 align-middle">
          {{ config.title }}
          <a
            v-if="config.helpUrl"
            :href="config.helpUrl"
            target="_blank"
          >
            <i class="fa-regular fa-circle-question" />
          </a>
        </div>
        <div class="cell small-12 medium-auto align-middle">
          <div class="button-group">
            <span
              v-for="option in config.options"
              :key="option.value"
              class="config-option-tooltip"
              :class="{ 'is-disabled': isBoardConfigDisabled(config.key, option.value) }"
            >
              <button
                type="button"
                class="button secondary hollow pill-button"
                :class="{ 'active': isBoardConfigActive(config.key, option.value) }"
                :disabled="isBoardConfigDisabled(config.key, option.value)"
                @click="setBoardConfigValue(config.key, option.value)"
              >
                <span
                  v-if="option.color"
                  class="color-indicator"
                  :style="{ backgroundColor: option.color }"
                />
                <span v-html="option.label" />
              </button>
              <span
                v-if="
                  isBoardConfigDisabled(config.key, option.value)
                "
                class="config-option-tooltip-text"
              >
                {{ boardConfigDisabledMessage(config.key, option.value) }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <!-- Service -->
      <div class="grid-x grid-margin-x align-middle">
        <div class="cell small-12 medium-4 align-middle">
          {{ $t('BoardPriceCalculator.service') }}
        </div>
        <div class="cell small-12 medium-auto align-middle">
          <div class="button-group">
            <button
              v-for="option in serviceOptions"
              :key="String(option.value)"
              type="button"
              class="button secondary hollow pill-button"
              :class="{ 'active': blitz === option.value }"
              @click="setService(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Section (right) -->
    <div class="cell small-12 medium-4 margin-top-1">
      <div ref="summaryCard" class="summary-card">
        <!-- Error state -->
        <div v-if="error" class="summary-cta">
          <i class="fa fa-exclamation-circle margin-right-1" />
          <span v-html="error" />
        </div>

        <div v-else-if="displayPrice">
          <!-- Price -->
          <div class="summary-item">
            <h3 class="summary-item-label h4">
              {{ $t('BoardPriceCalculator.priceForQuantity', { qty: displayPrice.quantity }) }}
            </h3>
            <span class="summary-item-value-row">
              <span class="summary-item-value summary-item-value-large">{{ displayPrice.grossPrice }}</span>
              <span class="summary-item-hint">
                {{ $t('BoardPriceCalculator.vatHint', { rate: displayPrice.taxRate, amount: displayPrice.taxAmount }) }}
                <span class="vat-tooltip">
                  <span class="vat-tooltip">
                    <i class="fa fa-info-circle" />
                    <span class="vat-tooltip-text">{{ $t('BoardPriceCalculator.vatEstimateHint') }}</span>
                  </span>
                </span>
              </span>
            </span>
          </div>

          <!-- Unit Price -->
          <div class="summary-item">
            <h3 class="summary-item-label h4">
              {{ $t('BoardPriceCalculator.unitPrice') }}
            </h3>
            <span class="summary-item-value-row">
              <span class="summary-item-value">{{ displayPrice.grossUnitPrice }}</span>
              <span class="summary-item-hint">
                {{
                  $t('BoardPriceCalculator.vatHint',
                     { rate: displayPrice.taxRate, amount: displayPrice.unitTaxAmount })
                }}
                <span class="vat-tooltip">
                  <i class="fa fa-info-circle" />
                  <span class="vat-tooltip-text">{{ $t('BoardPriceCalculator.vatEstimateHint') }}</span>
                </span>
              </span>
            </span>
          </div>

          <!-- Estimated Dispatch -->
          <div class="summary-item">
            <h3 class="summary-item-label h4">
              {{ $t('BoardPriceCalculator.estimatedDispatch') }}
            </h3>
            <span class="summary-item-value">
              {{ $t('BoardPriceCalculator.dispatchDays', { count: displayPrice.estimatedDispatchDays }) }}
            </span>
          </div>
        </div>

        <!-- CTA -->
        <div class="summary-cta">
          <p v-if="!displayPrice && !error">
            {{ $t('BoardPriceCalculator.inputNotice') }}
          </p>
          <p>{{ displayPrice ? $t('BoardPriceCalculator.soundsGood') : $t('BoardPriceCalculator.ctaNoPrice') }}</p>
          <a
            :href="newProjectPath"
            class="button primary expanded"
          >
            {{ $t('BoardPriceCalculator.startProject') }}
          </a>
        </div>

        <div class="flex-dir-row margin-top-1 text-center">
          <img
            src="~images/locale/eu_small.svg"
            class="inline-flag"
            :alt="$t('BoardPriceCalculator.euFlag')"
          >
          &nbsp;{{ $t('BoardPriceCalculator.madeInEurope') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { getRequest } from 'helper/request';
import {
  boardConfigDisabledMessage,
  buildBoardConfigOptions,
  isBoardConfigDisabled as isBoardConfigOptionDisabled
} from 'helper/boardConfigOptions';
import boardPriceMessages from 'locales/BoardPriceCalculator';
import boardConfigMessages from 'locales/BoardConfig';
import boardConfigOptionsMessages from 'locales/BoardConfigOptions';

export default {
  name: 'BoardPriceCalculator',

  props: {
    newProjectPath: {
      type: String,
    },
  },

  data() {
    return {
      boardConfigOptions: buildBoardConfigOptions(this.$t),
      layerOptions: [2, 4, 6, 8],
      width: '',
      height: '',
      layerCount: 2,
      quantity: null,
      blitz: false,
      displayPrice: null,
      error: null,
      configurationUnavailable: false,
      selectedBoardConfig: {
        pcbThickness: '1.6mm',
        surfaceFinish: 'ENIG',
        copperThickness: '35um',
        soldermaskColor: 'green',
        silkscreenColor: 'white',
      },
    };
  },

  computed: {
    canSubmit() {
      return this.width > 0 && this.height > 0 && this.quantity > 0;
    },

    serviceOptions() {
      return [
        { value: false, label: this.$t('BoardPriceCalculator.budget') },
        { value: true, label: this.$t('BoardPriceCalculator.blitz') },
      ];
    },
  },

  methods: {
    formatPrice(amount, currency) {
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency: currency,
      }).format(amount);
    },

    boardPricePayload() {
      return {
        width: this.width,
        height: this.height,
        layer_count: this.layerCount,
        quantity: this.quantity,
        surface_finish: this.selectedBoardConfig.surfaceFinish,
        soldermask_color: this.selectedBoardConfig.soldermaskColor,
        silkscreen_color: this.selectedBoardConfig.silkscreenColor,
        copper_thickness: this.selectedBoardConfig.copperThickness,
        pcb_thickness: this.selectedBoardConfig.pcbThickness,
        blitz: this.blitz,
        locale: this.$i18n.locale,
      };
    },

    debouncedFetchPrices: _.debounce(function () {
      this.fetchPrices();
    }, 400),

    async fetchPrices() {
      if (!this.canSubmit) return;

      this.error = null;
      this.configurationUnavailable = false;

      try {
        const response = await getRequest('/meta/board_price', { query: this.boardPricePayload() });

        if (response?.error) {
          if (response.error === 'not_supported') {
            this.configurationUnavailable = true;
            this.error = this.$t('BoardPriceCalculator.notSupported');
          } else {
            this.error = this.$t('BoardPriceCalculator.genericError');
          }
          this.displayPrice = null;
          return;
        }

        const currency = response.currency;
        this.displayPrice = {
          netPrice: this.formatPrice(response.net_amount, currency),
          netUnitPrice: this.formatPrice(response.net_unit_price, currency),
          grossPrice: this.formatPrice(response.gross_amount, currency),
          grossUnitPrice: this.formatPrice(response.gross_unit_price, currency),
          taxAmount: this.formatPrice(response.tax_amount, currency),
          unitTaxAmount: this.formatPrice(response.unit_tax, currency),
          taxRate: response.tax_rate,
          estimatedDispatchDays: response.leadtime,
          quantity: response.quantity,
        };
      } catch {
        this.error = this.$t('BoardPriceCalculator.genericError');
        this.displayPrice = null;
      }
    },

    setLayerCount(layer) {
      this.layerCount = layer;
      this.enforceValidConfig();
      this.fetchPrices();
    },

    enforceValidConfig() {
      const updated = { ...this.selectedBoardConfig };

      for (const config of this.boardConfigOptions) {
        const currentValue = updated[config.key];
        if (this.isBoardConfigDisabled(config.key, currentValue, updated)) {
          const firstEnabled = config.options.find(
            (opt) => !this.isBoardConfigDisabled(config.key, opt.value, updated),
          );
          if (firstEnabled) {
            updated[config.key] = firstEnabled.value;
          }
        }
      }

      this.selectedBoardConfig = updated;
    },

    isBoardConfigActive(key, value) {
      return this.selectedBoardConfig[key] === value;
    },

    isBoardConfigDisabled(configOption, optionValue, selectedBoardConfig = this.selectedBoardConfig) {
      return isBoardConfigOptionDisabled({
        configOption,
        optionValue,
        selectedBoardConfig,
      });
    },

    boardConfigDisabledMessage(configOption, optionValue) {
      return boardConfigDisabledMessage({
        configOption,
        optionValue,
        selectedBoardConfig: this.selectedBoardConfig,
        t: this.$t,
      });
    },

    setBoardConfigValue(key, value) {
      this.selectedBoardConfig = {
        ...this.selectedBoardConfig,
        [key]: value,
      };
      this.enforceValidConfig();
      this.fetchPrices();
    },

    setService(blitz) {
      this.blitz = blitz;
      this.fetchPrices();
    },
  },

  i18n: {
    messages: _.merge({}, boardPriceMessages, boardConfigMessages, boardConfigOptionsMessages),
  },
};
</script>

<style scoped lang="scss">
@import 'stylesheets/foundation/colors';

.board-price-calculator {
  .button.pill-button {
    border-radius: 12px !important;
    margin: 0.25rem;
    color: #333;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &:hover {
      color: $orange;
    }

    &:focus {
      background-color: transparent;
      color: #333;
    }

    &.active {
      background-color: $orange;
      color: $white;
      border-color: $orange;
    }

    &.active:hover {
      background-color: $orange;
      color: $white;
    }

    &.active:focus {
      background-color: $orange;
      color: $white;
    }

    &:disabled {
      background-color: $light-gray !important;
      color: $dark-gray !important;
      border-color: #ddd !important;
      cursor: not-allowed;
      opacity: 1 !important;
    }
  }

  .title-row {
    display: flex;
    justify-content: center;
    align-items: baseline;
    gap: 0.75rem;
  }

  .color-indicator {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 12px;
    margin-right: 5px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    vertical-align: middle;
  }

  .inline-input {
    max-width: 10rem;
    margin-bottom: 0.5rem;
  }

  .dimensions-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .dimensions-separator {
    color: $dark-gray;
    font-size: 1rem;
    line-height: 1;
  }

  .summary-card {
    background: $body-background;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .summary-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;

    & + & {
      margin-top: 1.25rem;
      padding-top: 1.25rem;
      border-top: 1px solid #e0e0e0;
    }

    &-label {
      font-size: 1rem;
      text-transform: uppercase;
      margin-bottom: 0.2rem;
    }

    &-value-row {
      display: flex;
      align-items: baseline;
      gap: 0.5rem;
    }

    &-value {
      font-size: 1rem;
      margin-bottom: 0.25rem;

      &-large {
        font-size: 1.75rem;
        font-weight: 700;
      }

      a {
        color: $orange;
      }
    }

    &-hint {
      font-size: 0.85rem;
      margin-bottom: 0.5rem;
    }
  }

  .config-option-tooltip {
    position: relative;
    display: inline-flex;

    &-text {
      visibility: hidden;
      opacity: 0;
      position: absolute;
      bottom: calc(100% + 6px);
      left: 50%;
      transform: translateX(-50%);
      background: $black;
      color: $white;
      font-size: 0.85rem;
      padding: 0.35rem 0.6rem;
      border-radius: 4px;
      white-space: normal;
      width: 14rem;
      text-align: center;
      transition: opacity 0.15s ease;
      z-index: 10;
      pointer-events: none;
    }

    &.is-disabled:hover .config-option-tooltip-text {
      visibility: visible;
      opacity: 1;
    }
  }

  .vat-tooltip {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: default;

    &:hover .vat-tooltip-text {
      visibility: visible;
      opacity: 1;
    }

    &-text {
      visibility: hidden;
      opacity: 0;
      position: absolute;
      bottom: calc(100% + 6px);
      right: 0;
      transform: none;
      background: $black;
      color: $white;
      font-size: 0.9rem;
      padding: 0.35rem 0.6rem;
      border-radius: 4px;
      white-space: normal;
      width: 12rem;
      transition: opacity 0.15s ease;
      z-index: 10;
    }
  }

  .summary-cta {
    padding-top: 1.25rem;
    text-align: center;

    p {
      margin-bottom: 0.75rem;
    }
  }

  .inline-flag {
    height: 1rem;
    border-radius: 0.2rem;
  }
}
</style>
